import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Volume2, VolumeX, Play, Pause, Waves, Wind, 
  Cloud, Trees, Music, Moon, Droplets, Bird, Flame
} from 'lucide-react';
import { Howl } from 'howler';

interface SoundTherapyProps {
  onClose: () => void;
}

interface Sound {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  url: string;
  category: 'nature' | 'ambient' | 'meditation';
}

const sounds: Sound[] = [
  {
    id: 'ocean-waves',
    name: 'Ocean Waves',
    icon: <Waves className="w-5 h-5" />,
    color: 'from-blue-500 to-cyan-500',
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-ocean-waves-loop-1196.mp3',
    category: 'nature'
  },
  {
    id: 'gentle-rain',
    name: 'Gentle Rain',
    icon: <Cloud className="w-5 h-5" />,
    color: 'from-blue-400 to-indigo-500',
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-light-rain-loop-2393.mp3',
    category: 'nature'
  },
  {
    id: 'forest',
    name: 'Forest Birds',
    icon: <Bird className="w-5 h-5" />,
    color: 'from-green-500 to-emerald-500',
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-forest-birds-loop-1254.mp3',
    category: 'nature'
  },
  {
    id: 'wind',
    name: 'Gentle Wind',
    icon: <Wind className="w-5 h-5" />,
    color: 'from-sky-400 to-blue-500',
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-blizzard-wind-loop-1153.mp3',
    category: 'nature'
  },
  {
    id: 'stream',
    name: 'Stream',
    icon: <Droplets className="w-5 h-5" />,
    color: 'from-cyan-500 to-blue-500',
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-river-stream-loop-1249.mp3',
    category: 'nature'
  },
  {
    id: 'night',
    name: 'Night Ambience',
    icon: <Moon className="w-5 h-5" />,
    color: 'from-indigo-500 to-purple-500',
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-night-crickets-loop-1164.mp3',
    category: 'ambient'
  },
  {
    id: 'campfire',
    name: 'Campfire',
    icon: <Flame className="w-5 h-5" />,
    color: 'from-orange-500 to-red-500',
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-campfire-crackles-1330.mp3',
    category: 'ambient'
  },
  {
    id: 'meditation',
    name: 'Meditation Bowl',
    icon: <Music className="w-5 h-5" />,
    color: 'from-purple-500 to-pink-500',
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-meditation-bell-595.mp3',
    category: 'meditation'
  }
];

export function SoundTherapy({ onClose }: SoundTherapyProps) {
  const [activeSounds, setActiveSounds] = useState<Record<string, Howl>>({});
  const [volumes, setVolumes] = useState<Record<string, number>>({});
  const [selectedCategory, setSelectedCategory] = useState<'all' | Sound['category']>('all');
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    // Initialize Howl instances for each sound
    const soundInstances: Record<string, Howl> = {};
    sounds.forEach(sound => {
      soundInstances[sound.id] = new Howl({
        src: [sound.url],
        loop: true,
        volume: 0.5,
        html5: true
      });
    });

    setActiveSounds(soundInstances);
    const initialVolumes: Record<string, number> = {};
    sounds.forEach(sound => {
      initialVolumes[sound.id] = 0;
    });
    setVolumes(initialVolumes);

    return () => {
      Object.values(soundInstances).forEach(sound => sound.unload());
    };
  }, []);

  const toggleSound = (soundId: string) => {
    const sound = activeSounds[soundId];
    const currentVolume = volumes[soundId];

    if (currentVolume > 0) {
      sound.fade(currentVolume, 0, 500);
      setVolumes(prev => ({ ...prev, [soundId]: 0 }));
      sound.stop();
    } else {
      sound.volume(0);
      sound.play();
      sound.fade(0, 0.5, 500);
      setVolumes(prev => ({ ...prev, [soundId]: 0.5 }));
    }
  };

  const handleVolumeChange = (soundId: string, value: number) => {
    const sound = activeSounds[soundId];
    sound.volume(value);
    setVolumes(prev => ({ ...prev, [soundId]: value }));
  };

  const toggleAllSounds = () => {
    const newIsPlaying = !isPlaying;
    setIsPlaying(newIsPlaying);

    Object.entries(activeSounds).forEach(([soundId, sound]) => {
      if (newIsPlaying) {
        if (volumes[soundId] > 0) {
          sound.play();
        }
      } else {
        sound.pause();
      }
    });
  };

  const filteredSounds = sounds.filter(
    sound => selectedCategory === 'all' || sound.category === selectedCategory
  );

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto mindful-scrollbar">
      <div className="min-h-screen p-6">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between sticky top-0 bg-background/50 backdrop-blur-sm py-4 z-50">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Volume2 className="w-6 h-6 text-primary" />
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
              </div>
              <h2 className="text-xl font-semibold">Sound Therapy</h2>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Category Filter */}
          <div className="flex justify-center gap-4">
            {['all', 'nature', 'ambient', 'meditation'].map((category) => (
              <motion.button
                key={category}
                onClick={() => setSelectedCategory(category as typeof selectedCategory)}
                className={`px-4 py-2 rounded-lg ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/70'
                } transition-colors`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="capitalize">{category}</span>
              </motion.button>
            ))}
          </div>

          {/* Master Control */}
          <div className="flex justify-center">
            <motion.button
              onClick={toggleAllSounds}
              className="theme-button px-6 py-3 rounded-lg flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isPlaying ? (
                <>
                  <Pause className="w-5 h-5" />
                  Pause All
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Play All
                </>
              )}
            </motion.button>
          </div>

          {/* Sound Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredSounds.map((sound) => (
                <motion.div
                  key={sound.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="theme-card p-6 relative overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${sound.color} opacity-5`} />
                  <div className="relative space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {sound.icon}
                          <div className="absolute inset-0 bg-current opacity-20 blur-lg rounded-full" />
                        </div>
                        <span className="font-medium">{sound.name}</span>
                      </div>
                      <motion.button
                        onClick={() => toggleSound(sound.id)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className={`p-2 rounded-lg ${
                          volumes[sound.id] > 0
                            ? 'bg-primary/20 text-primary'
                            : 'bg-muted hover:bg-muted/70'
                        }`}
                      >
                        {volumes[sound.id] > 0 ? (
                          <Volume2 className="w-5 h-5" />
                        ) : (
                          <VolumeX className="w-5 h-5" />
                        )}
                      </motion.button>
                    </div>

                    {volumes[sound.id] > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={volumes[sound.id]}
                          onChange={(e) => handleVolumeChange(sound.id, parseFloat(e.target.value))}
                          className="w-full accent-primary"
                        />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}